import { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { usePrepareFormTagsForSetReportPayload } from 'hooks/usePrepareFormTagsForSetReportPayload';

import { ReportTemplateBase, QueryParams as createTemplateParams } from 'api/endPoints/CreateNewTemplateFromReport';
import { QueryParams as overwriteTemplateParams } from 'api/endPoints/OverwriteTemplateFromReport';

import { UseCreateTemplateFromReportModalProvider } from 'components/Dashboards/Reporting/CreateTemplateFromReportModal/CreateTemplateFromReportModal.provider';
import { FormValues } from 'components/Dashboards/Reporting/CreateTemplateFromReportModal/CreateTemplateFromReportModal.types';
import { optionsToTags } from 'components/Dashboards/Reporting/ReportSettingsModal/components/UpdateReportTags/UpdateReportTags.utils';
import { ReportPageUrlParams } from 'components/pages/ReportPage/ReportPage';

export type UseCreateTemplateFromReportModalLogic = ReturnType<typeof useCreateTemplateFromReportModalLogic>;

interface Props {
    api: UseCreateTemplateFromReportModalProvider['api'];
    setIsModalOpen: (value: boolean) => void;
}

export const useCreateTemplateFromReportModalLogic = ({ api, setIsModalOpen }: Props) => {
    const { reportId } = useParams<ReportPageUrlParams>();
    const [radioOption, setRadioOption] = useState<'overwrite' | 'new'>('new');
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const queryClient = useQueryClient();
    const { getTagsPayloadForReport } = usePrepareFormTagsForSetReportPayload();

    const { mutateAsync: createTemplateMutationAsync } = api.createTemplateFromReport.instance;
    const { mutateAsync: overwriteTemplateMutationAsync } = api.overwriteTemplateFromReport.instance;

    const form = useForm<FormValues>({
        defaultValues: {
            templateDescription: '',
            templateId: null,
            templateName: '',
            reportId: Number(reportId),
            selectedTags: [],
        },
    });

    const hideModal = () => {
        resetFormToInitialState();
        setRadioOption('new');
        setIsModalOpen(false);
    };

    const selectRadioOption = (option: 'overwrite' | 'new') => {
        setRadioOption(option);
        // when moving to the overwrite option, there is no selected template, so we don't have tags yet.
        const selectedTags = option === 'new' ? api.getReport.data?.tags : [];
        form.reset(
            {
                templateDescription: '',
                templateName: '',
                reportId: Number(reportId),
                templateId: null,
                selectedTags,
            },
            {
                keepDirty: false,
                keepTouched: false,
            },
        );
    };

    const resetFormToInitialState = useCallback(() => {
        const selectedTags = api.getReport.data?.tags ?? [];
        form.reset(
            {
                reportId: Number(reportId),
                templateName: '',
                templateId: null,
                templateDescription: '',
                selectedTags,
            },
            { keepDirty: false, keepTouched: false },
        );
    }, [api.getReport.data, reportId, form]);

    // show tags only when in new template mode or overwrite template with selected template.
    const shouldShowTags =
        radioOption === 'new' || (radioOption === 'overwrite' && form.getValues('templateId') !== null);

    // In case the user creates a new template then tags are the report tags.
    // In case the user overwrites a template then tags are the template tags.
    const getTemplateTags = () => {
        if (radioOption === 'new') {
            return api.getReport.data?.tags;
        } else if (radioOption === 'overwrite') {
            const templateId = form.getValues('templateId');
            const template = api.getReportTemplates.data?.find((template) => template.id === templateId);
            return template?.tags ?? [];
        } else {
            return [];
        }
    };

    type UserAction = 'CreateNewTemplate' | 'OverwriteTemplate';
    const handleTemplateAction = async (data: FormValues, action: UserAction) => {
        const baseTemplatePayload: ReportTemplateBase = {
            reportId: data.reportId,
            name: data.templateName,
            description: data.templateDescription,
            tags: data.selectedTags.map((tag) => tag.id),
        };
        const payload: createTemplateParams | overwriteTemplateParams =
            action === 'CreateNewTemplate'
                ? ({
                      ...baseTemplatePayload,
                  } as createTemplateParams)
                : ({
                      ...baseTemplatePayload,
                      templateId: data.templateId,
                  } as overwriteTemplateParams);

        const mutateAsync =
            action === 'CreateNewTemplate' ? createTemplateMutationAsync : overwriteTemplateMutationAsync;
        await mutateAsync(payload, {
            onSuccess: async () => {
                if (action === 'CreateNewTemplate') {
                    sendEventWithDimensions({
                        category: 'Reporting',
                        action: 'Template Created from Report',
                        label: 'None',
                    });
                }
                const successMessage =
                    action === 'CreateNewTemplate'
                        ? 'Template created successfully'
                        : 'Template has overwritten successfully';
                handleSnackBar(successMessage, 'success');
                await Promise.all([
                    queryClient.invalidateQueries('getReportTemplateList'),
                    queryClient.invalidateQueries('getAllReportTags'),
                ]);

                hideModal();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const handleSubmitForm = async (data: FormValues) => {
        const reportTagsPayload = await getTagsPayloadForReport(data.selectedTags);
        const formData = { ...data, selectedTags: reportTagsPayload };
        handleTemplateAction(formData, radioOption === 'new' ? 'CreateNewTemplate' : 'OverwriteTemplate');
    };

    const templateOptions: ISelectOption<number>[] =
        api.getReportTemplates.data?.map((template) => {
            return {
                value: template.id,
                label: template.name,
            };
        }) ?? [];

    const handleTagsChange = (value: readonly ISelectOption<number>[]) => {
        const selectedTags = optionsToTags(value);
        form.setValue('selectedTags', selectedTags, { shouldDirty: true });
    };

    useEffect(() => {
        if (reportId && api.getReport.data?.tags) {
            form.setValue('selectedTags', api.getReport.data.tags, { shouldDirty: false });
        }
    }, [reportId, api.getReport.data?.tags]);

    const buttonText = radioOption === 'new' ? 'Create new Template' : 'Overwrite existing Template';

    return {
        logic: {
            setRadioToNewTemplate: () => selectRadioOption('new'),
            setRadioToOverwrite: () => selectRadioOption('overwrite'),
            handleSubmitForm,
            form,
            hideModal,
            templateOptions,
            handleTagsChange,
            buttonText,
            radioOption,
            getTemplateTags,
            shouldShowTags,
        },
    };
};
